import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatSource } from "../stores/chatStore";
import { generateSourceUrl } from "../lib/chat-utils";
import { useOrgStore } from "../stores/orgStore";
import { ExternalLink } from "lucide-react";
import "./MessageReferences.css";

interface MessageWithReferencesProps {
  content: string;
  sources?: ChatSource[];
  className?: string;
}

/**
 * Component that renders markdown content with clickable reference links [1], [2], etc.
 */
export function MessageWithReferences({
  content,
  sources = [],
  className = "",
}: MessageWithReferencesProps) {
  const { currentOrgId } = useOrgStore();

  // Debug: log content being rendered
  console.log("[MessageWithReferences] Rendering content:", content?.length, "chars, content:", content?.substring(0, 100));

  // Process children recursively to replace references in text nodes
  const processChildren = (children: any): any => {
    if (typeof children === 'string') {
      return replaceReferencesInText(children);
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => {
        if (typeof child === 'string') {
          return <span key={index}>{replaceReferencesInText(child)}</span>;
        }
        return child;
      });
    }

    return children;
  };

  // Replace [1], [2] patterns with clickable links
  const replaceReferencesInText = (text: string) => {
    if (!sources || sources.length === 0 || !currentOrgId) {
      return text;
    }

    const referencePattern = /\[(\d+)\]/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let matchIndex = 0;

    while ((match = referencePattern.exec(text)) !== null) {
      // Add text before the reference
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Add the reference link
      const refNumber = parseInt(match[1], 10);
      const sourceIndex = refNumber - 1;
      const source = sources[sourceIndex];

      if (source && currentOrgId) {
        const url = generateSourceUrl(source, currentOrgId);
        if (url) {
          parts.push(
            <a
              key={`ref-${matchIndex}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-reference-link"
              title={`View ${source.sourceType}: ${source.content.substring(0, 100)}...`}
            >
              [{refNumber}]
              <ExternalLink className="chat-reference-icon" />
            </a>
          );
        } else {
          parts.push(<span key={`ref-${matchIndex}`} className="chat-reference-missing">[{refNumber}]</span>);
        }
      } else {
        parts.push(<span key={`ref-${matchIndex}`} className="chat-reference-missing">[{refNumber}]</span>);
      }

      lastIndex = match.index + match[0].length;
      matchIndex++;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 1 ? parts : text;
  };

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="chat-p">{processChildren(children)}</p>,
          ul: ({ children }) => <ul className="chat-ul">{children}</ul>,
          ol: ({ children }) => <ol className="chat-ol">{children}</ol>,
          li: ({ children }) => <li className="chat-li">{processChildren(children)}</li>,
          code: ({ inline, children }) =>
            inline ? (
              <code className="chat-code-inline">{children}</code>
            ) : (
              <code className="chat-code-block">{children}</code>
            ),
          strong: ({ children }) => <strong className="chat-strong">{processChildren(children)}</strong>,
          em: ({ children }) => <em className="chat-em">{processChildren(children)}</em>,
          h1: ({ children }) => <h1 className="chat-h1">{processChildren(children)}</h1>,
          h2: ({ children }) => <h2 className="chat-h2">{processChildren(children)}</h2>,
          h3: ({ children }) => <h3 className="chat-h3">{processChildren(children)}</h3>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
